/*
 * WorksheetsConsole
 * ─────────────────
 * Admin console for the worksheets library.
 *
 * Left column  : drag-and-drop bulk uploader — accepts unlimited files at
 *                once. Also supports pre-attaching module_slugs to every
 *                file in the batch (multi-select checkbox list).
 * Right column : searchable table of every worksheet with edit + delete
 *                actions. Editing lets the admin rename, tag, and pick
 *                which modules the worksheet appears under.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, formatError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import {
  Upload, FileText, Trash2, Loader2, Search, Pencil, X, Check,
  BookOpen, FolderOpen, Download, Plus, Sparkles, ShieldAlert,
  ChevronDown, ChevronRight, Lock, ArrowRight,
} from "lucide-react";

function fmtSize(n) {
  if (!n) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

export default function WorksheetsConsole() {
  const { user } = useAuth();
  const isSuperAdmin = (user?.role || "").toLowerCase() === "super_admin";
  const [worksheets, setWorksheets] = useState([]);
  const [workbookRows, setWorkbookRows] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [query, setQuery] = useState("");
  const [editRow, setEditRow] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);   // two-step delete target
  const [batchSlugs, setBatchSlugs] = useState([]);
  // Collapsed module ids — everything expanded by default so admins see
  // the full picture on first load. Persisted per-session in memory only.
  const [collapsed, setCollapsed] = useState(() => new Set());
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/worksheets")
      .then((r) => setWorksheets(Array.isArray(r.data) ? r.data : []))
      .catch((err) => toast.error(formatError(err)))
      .finally(() => setLoading(false));
    api.get("/modules")
      .then((r) => setModules(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
    // Interactive Course Workbook worksheets — read-only in this
    // console. Rendered inline under each module so admins see BOTH
    // the file library AND the structured Q&A worksheets in one place.
    api.get("/admin/workbook-worksheets")
      .then((r) => setWorkbookRows(Array.isArray(r.data) ? r.data : []))
      .catch(() => {}); // silent fail — workbook rows are additive
  };
  useEffect(load, []);

  const filtered = useMemo(() => {
    if (!query.trim()) return worksheets;
    const q = query.toLowerCase();
    return worksheets.filter((w) =>
      (w.title || "").toLowerCase().includes(q) ||
      (w.original_filename || "").toLowerCase().includes(q) ||
      (w.description || "").toLowerCase().includes(q) ||
      (w.module_slugs || []).some((s) => s.toLowerCase().includes(q))
    );
  }, [worksheets, query]);

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      if (batchSlugs.length) fd.append("module_slugs", batchSlugs.join(","));
      const r = await api.post("/admin/worksheets/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Uploaded ${r.data?.uploaded || files.length} worksheet${files.length === 1 ? "" : "s"}`);
      load();
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = (worksheet) => {
    // Non-super-admins never reach the button, but guard the entry
    // point anyway so a keyboard shortcut / dev-tools nudge can't skip.
    if (!isSuperAdmin) {
      toast.error("Only super admins can delete worksheets.");
      return;
    }
    setDeleteRow(worksheet);
  };

  // Group worksheets by module. Each module gets its own bucket; a
  // worksheet attached to N modules shows up under every one of them
  // (mirrors how members see it — a "PnL Template" attached to Module 4
  // AND Module 12 appears in both admin sections too). Anything without
  // an attachment lands in a special "Unassigned" bucket at the bottom
  // so admins can see + fix orphans immediately.
  const grouped = useMemo(() => {
    const byModule = new Map();
    for (const m of modules) byModule.set(m.slug, { module: m, items: [] });
    const unassigned = [];
    for (const w of filtered) {
      const slugs = (w.module_slugs || []).filter(Boolean);
      if (slugs.length === 0) {
        unassigned.push(w);
        continue;
      }
      for (const s of slugs) {
        if (byModule.has(s)) {
          byModule.get(s).items.push(w);
        } else {
          // Worksheet attached to a slug we don't have a module doc for
          // — surface it in Unassigned so it isn't invisible.
          unassigned.push(w);
          break;
        }
      }
    }
    // Preserve module order (sorted by `order` upstream), drop empty
    // modules ONLY when the admin is actively searching so the module
    // list doesn't stay static-looking under filters.
    const isSearching = !!query.trim();
    const rows = [];
    for (const [slug, bucket] of byModule) {
      if (isSearching && bucket.items.length === 0) continue;
      rows.push(bucket);
    }
    if (unassigned.length > 0 || !isSearching) {
      rows.push({ module: { slug: "__unassigned__", name: "Unassigned worksheets" }, items: unassigned });
    }
    return rows;
  }, [filtered, modules, query]);

  const toggleGroup = (slug) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug); else next.add(slug);
      return next;
    });
  };

  // Group workbook worksheets by module slug once, so per-row rendering
  // stays O(1). Empty array when the endpoint hasn't resolved yet.
  const workbookByModule = useMemo(() => {
    const m = new Map();
    for (const w of workbookRows) {
      const slug = w.module_slug;
      if (!slug) continue;
      if (!m.has(slug)) m.set(slug, []);
      m.get(slug).push(w);
    }
    return m;
  }, [workbookRows]);

  return (
    <div className="grid lg:grid-cols-[380px_1fr] gap-6" data-testid="admin-worksheets">
      {/* LEFT: uploader */}
      <div className="card-elevated p-6 h-fit space-y-4">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-primary font-semibold inline-flex items-center gap-1">
            <Upload className="w-3 h-3" /> Bulk upload
          </p>
          <h2 className="font-display text-2xl mt-1">Add worksheets</h2>
          <p className="text-xs text-muted-foreground mt-2">
            Drop multiple files at once — PDF, DOCX, XLSX, PPTX, CSV, TXT, MD, PNG, JPG, ZIP.
          </p>
        </div>

        {/* Pre-attach modules */}
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
            Attach to modules (optional)
          </label>
          <div
            className="mt-1 max-h-40 overflow-auto border border-shell-border rounded-lg p-2 space-y-1 bg-background"
            data-testid="ws-batch-modules"
          >
            {modules.length === 0 ? (
              <p className="text-xs text-muted-foreground py-1 px-1">No modules yet.</p>
            ) : modules.map((m) => {
              const on = batchSlugs.includes(m.slug);
              return (
                <label key={m.slug} className="flex items-start gap-2 text-xs cursor-pointer hover:bg-muted/40 rounded px-1.5 py-1">
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={(e) => setBatchSlugs((s) => e.target.checked ? [...s, m.slug] : s.filter((x) => x !== m.slug))}
                    data-testid={`ws-batch-mod-${m.slug}`}
                    className="mt-0.5 accent-primary"
                  />
                  <span className="flex-1">
                    <span className="font-medium">{m.name}</span>
                    <span className="text-muted-foreground"> · {m.slug}</span>
                  </span>
                </label>
              );
            })}
          </div>
          {batchSlugs.length > 0 && (
            <p className="text-[10px] text-primary font-semibold mt-1">
              Will attach to {batchSlugs.length} module{batchSlugs.length === 1 ? "" : "s"} on upload
              <button onClick={() => setBatchSlugs([])} className="ml-2 underline">clear</button>
            </p>
          )}
        </div>

        {/* Drop zone */}
        <label
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
          data-testid="ws-dropzone"
          className={[
            "block border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors",
            dragOver ? "border-primary bg-primary/5" : "border-shell-border bg-background hover:border-primary/40",
          ].join(" ")}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.csv,.xlsx,.xls,.docx,.doc,.pptx,.ppt,.txt,.md,.png,.jpg,.jpeg,.zip"
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
            data-testid="ws-file-input"
            disabled={uploading}
          />
          {uploading ? (
            <div className="text-primary inline-flex items-center gap-2 font-semibold text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> Uploading…
            </div>
          ) : (
            <>
              <Upload className="w-6 h-6 mx-auto text-primary" />
              <p className="mt-2 text-sm font-semibold">Drop files here or click to browse</p>
              <p className="text-[11px] text-muted-foreground mt-1">Any number of files at once</p>
            </>
          )}
        </label>
      </div>

      {/* RIGHT: list */}
      <div className="card-elevated p-4">
        <div className="flex items-center gap-2 mb-3">
          <div className="flex-1 relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search worksheets…"
              data-testid="ws-search"
              className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-shell-border bg-white focus:outline-none focus:border-primary/40"
            />
          </div>
          <span className="text-xs text-muted-foreground" data-testid="ws-count">
            {loading ? "Loading…" : `${filtered.length} of ${worksheets.length}`}
          </span>
        </div>

        {loading ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
        ) : (worksheets.length === 0 && !query.trim()) ? (
          // Cold start — no worksheets in the library yet. Show every
          // module as an empty target so the admin sees the full
          // module list and knows where uploads will land.
          <>
            <div className="mb-3 p-3 rounded-lg border border-dashed border-shell-border bg-muted/40 text-xs text-muted-foreground" data-testid="ws-empty">
              <span className="font-semibold">Two worksheet types live here:</span> interactive
              Course-Workbook worksheets (already seeded from the LMS) show up per module below;
              drop file uploads (PDF, DOCX, XLSX…) on the left to add downloadable templates.
            </div>
            <div className="space-y-3" data-testid="ws-module-groups">
              {modules.map((m) => {
                const open = !collapsed.has(m.slug);
                const wbRows = workbookByModule.get(m.slug) || [];
                return (
                  <div
                    key={m.slug}
                    data-testid={`ws-group-${m.slug}`}
                    className="rounded-lg border border-shell-border bg-white overflow-hidden"
                  >
                    <button
                      type="button"
                      onClick={() => toggleGroup(m.slug)}
                      data-testid={`ws-group-toggle-${m.slug}`}
                      className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-muted/60 text-left"
                    >
                      {open ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                      <div className="flex-1 min-w-0 flex items-center gap-2">
                        <BookOpen className="w-4 h-4 text-primary shrink-0" />
                        <span className="font-semibold text-sm truncate">{m.name}</span>
                        <span className="text-[11px] text-muted-foreground truncate">· {m.slug}</span>
                      </div>
                      <span className="text-[11px] font-semibold text-muted-foreground shrink-0">
                        {wbRows.length > 0 ? `${wbRows.length} workbook · 0 files` : "0 worksheets"}
                      </span>
                    </button>
                    {open && (
                      <>
                        <WorkbookWorksheetList rows={wbRows} moduleSlug={m.slug} />
                        <div className="px-4 py-3 text-xs text-muted-foreground italic border-t border-shell-border">
                          No file worksheets attached yet. Upload one on the left and tick this module, or edit an existing worksheet to attach it here.
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground" data-testid="ws-empty">
            No matches for your search.
          </div>
        ) : (
          <div className="space-y-3" data-testid="ws-module-groups">
            {grouped.map(({ module: m, items }) => {
              const isUnassigned = m.slug === "__unassigned__";
              const open = !collapsed.has(m.slug);
              const wbRows = isUnassigned ? [] : (workbookByModule.get(m.slug) || []);
              return (
                <div
                  key={m.slug}
                  data-testid={`ws-group-${m.slug}`}
                  className={[
                    "rounded-lg border overflow-hidden",
                    isUnassigned
                      ? "border-pink-300/70 bg-pink-50/40"
                      : "border-shell-border bg-white",
                  ].join(" ")}
                >
                  <button
                    type="button"
                    onClick={() => toggleGroup(m.slug)}
                    data-testid={`ws-group-toggle-${m.slug}`}
                    className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-muted/60 text-left"
                  >
                    {open ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        {isUnassigned ? (
                          <ShieldAlert className="w-4 h-4 text-pink-600 shrink-0" />
                        ) : (
                          <BookOpen className="w-4 h-4 text-primary shrink-0" />
                        )}
                        <span className="font-semibold text-sm truncate">{m.name}</span>
                        {!isUnassigned && (
                          <span className="text-[11px] text-muted-foreground truncate">· {m.slug}</span>
                        )}
                      </div>
                    </div>
                    <span className="text-[11px] font-semibold text-muted-foreground shrink-0" data-testid={`ws-group-count-${m.slug}`}>
                      {wbRows.length > 0
                        ? `${wbRows.length} workbook · ${items.length} file${items.length === 1 ? "" : "s"}`
                        : `${items.length} worksheet${items.length === 1 ? "" : "s"}`}
                    </span>
                  </button>
                  {open && (
                    <>
                      {/* Interactive Course-Workbook worksheets — always
                          rendered above file uploads so admins see the
                          canonical curriculum content first. */}
                      <WorkbookWorksheetList rows={wbRows} moduleSlug={m.slug} />
                      {items.length === 0 ? (
                      <div className="px-4 py-3 text-xs text-muted-foreground italic border-t border-shell-border">
                        No worksheets attached yet. Upload one on the left and tick this module, or edit an existing worksheet to attach it here.
                      </div>
                    ) : (
                      <ul className="divide-y divide-shell-border border-t border-shell-border">
                        {items.map((w) => (
                          <li key={`${m.slug}:${w.id}`} className="flex items-center gap-3 px-3 py-2" data-testid={`ws-row-${w.id}`}>
                            <span className="w-8 h-8 rounded-lg bg-primary/10 text-primary grid place-items-center flex-shrink-0">
                              <FileText className="w-4 h-4" />
                            </span>
                            <div className="min-w-0 flex-1">
                              <p className="font-semibold truncate text-sm">{w.title}</p>
                              <p className="text-[11px] text-muted-foreground truncate">
                                {w.original_filename} · {fmtSize(w.size)}
                                {(w.module_slugs || []).length > 1 && (
                                  <> · also in {(w.module_slugs || []).length - 1} other module{(w.module_slugs || []).length - 1 === 1 ? "" : "s"}</>
                                )}
                              </p>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              <a
                                href={`${process.env.REACT_APP_BACKEND_URL}/api/worksheets/${w.id}/file`}
                                target="_blank" rel="noreferrer"
                                data-testid={`ws-download-${w.id}`}
                                className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                                title="Open file"
                              >
                                <Download className="w-3.5 h-3.5" />
                              </a>
                              <button
                                onClick={() => setEditRow(w)}
                                data-testid={`ws-edit-${w.id}`}
                                className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                                title="Edit title, description, or module attachments"
                              >
                                <Pencil className="w-3.5 h-3.5" />
                              </button>
                              {isSuperAdmin ? (
                                <button
                                  onClick={() => handleDelete(w)}
                                  data-testid={`ws-delete-${w.id}`}
                                  className="p-1.5 rounded hover:bg-rose-500/10 text-rose-600"
                                  title="Delete worksheet (super admin only)"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              ) : (
                                <span
                                  data-testid={`ws-delete-locked-${w.id}`}
                                  className="p-1.5 text-muted-foreground/60 cursor-not-allowed"
                                  title="Deleting a worksheet requires super admin access"
                                >
                                  <Lock className="w-3.5 h-3.5" />
                                </span>
                              )}
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {editRow && (
        <WorksheetEditModal
          worksheet={editRow}
          modules={modules}
          onClose={() => setEditRow(null)}
          onSaved={() => { setEditRow(null); load(); }}
        />
      )}

      {deleteRow && (
        <WorksheetDeleteModal
          worksheet={deleteRow}
          onClose={() => setDeleteRow(null)}
          onDeleted={() => { setDeleteRow(null); load(); }}
        />
      )}
    </div>
  );
}

/* Edit modal — rename, describe, and pick module attachments */
function WorksheetEditModal({ worksheet, modules, onClose, onSaved }) {
  const [title, setTitle] = useState(worksheet.title || "");
  const [description, setDescription] = useState(worksheet.description || "");
  const [slugs, setSlugs] = useState(worksheet.module_slugs || []);
  const [busy, setBusy] = useState(false);

  const toggle = (slug) => {
    setSlugs((s) => s.includes(slug) ? s.filter((x) => x !== slug) : [...s, slug]);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!title.trim()) { toast.error("Title required"); return; }
    setBusy(true);
    try {
      await api.put(`/admin/worksheets/${worksheet.id}`, {
        title: title.trim(),
        description: description.trim(),
        module_slugs: slugs,
      });
      toast.success("Saved");
      onSaved();
    } catch (err) {
      toast.error(formatError(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-shell-side/60 backdrop-blur-sm grid place-items-center p-4" data-testid="ws-edit-modal">
      <form onSubmit={save} className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-shell-border flex items-start justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Worksheet</p>
            <h3 className="font-display text-xl mt-0.5">Edit</h3>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded hover:bg-muted">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-6 py-4 grid gap-4 max-h-[60vh] overflow-auto">
          <label className="grid gap-1">
            <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Title</span>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              data-testid="ws-edit-title"
              className="rounded-lg border border-shell-border px-3 py-2 text-sm focus:outline-none focus:border-primary/40"
            />
          </label>
          <label className="grid gap-1">
            <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Description</span>
            <textarea
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              data-testid="ws-edit-desc"
              className="rounded-lg border border-shell-border px-3 py-2 text-sm focus:outline-none focus:border-primary/40 resize-none"
            />
          </label>
          <div>
            <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Attach to modules</p>
            <div className="mt-1 max-h-56 overflow-auto border border-shell-border rounded-lg p-2 space-y-1 bg-background">
              {modules.map((m) => {
                const on = slugs.includes(m.slug);
                return (
                  <label key={m.slug} className="flex items-start gap-2 text-xs cursor-pointer hover:bg-muted/40 rounded px-1.5 py-1">
                    <input
                      type="checkbox" checked={on} onChange={() => toggle(m.slug)}
                      data-testid={`ws-edit-mod-${m.slug}`}
                      className="mt-0.5 accent-primary"
                    />
                    <span className="flex-1">
                      <span className="font-medium">{m.name}</span>
                      <span className="text-muted-foreground"> · {m.slug}</span>
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-shell-border flex items-center justify-end gap-2">
          <button type="button" onClick={onClose}
            className="text-xs font-semibold text-muted-foreground border border-shell-border rounded-full px-4 py-2 hover:text-foreground">
            Cancel
          </button>
          <button type="submit" disabled={busy}
            data-testid="ws-edit-save"
            className="btn-primary text-sm disabled:opacity-60 inline-flex items-center gap-1.5">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}

/* Two-step super-admin delete confirmation.
 * The admin must type "DELETE" verbatim before the delete button
 * enables. Mirrors the backend guard so a curl caller and a UI caller
 * hit the same friction. */
function WorksheetDeleteModal({ worksheet, onClose, onDeleted }) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const canDelete = typed.trim() === "DELETE";

  const submit = async (e) => {
    e.preventDefault();
    if (!canDelete) return;
    setBusy(true);
    try {
      await api.delete(`/admin/worksheets/${worksheet.id}`, {
        params: { confirm: "DELETE" },
      });
      toast.success(`Deleted "${worksheet.title}"`);
      onDeleted();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-shell-side/60 backdrop-blur-sm grid place-items-center p-4"
      data-testid="ws-delete-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <form
        onSubmit={submit}
        className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden"
      >
        <div className="px-6 py-4 border-b border-shell-border flex items-start justify-between bg-rose-50/60">
          <div className="flex items-start gap-3">
            <ShieldAlert className="w-5 h-5 text-rose-600 mt-0.5 shrink-0" />
            <div>
              <p className="text-[10px] uppercase tracking-widest text-rose-600 font-semibold">Super admin action</p>
              <h3 className="font-display text-xl mt-0.5">Delete worksheet</h3>
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded hover:bg-muted">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <p className="text-sm text-foreground">
            You're about to remove <span className="font-semibold">"{worksheet.title}"</span> from the library.
            Members enrolled in{" "}
            {(worksheet.module_slugs || []).length > 0 ? (
              <>{(worksheet.module_slugs || []).length} module{(worksheet.module_slugs || []).length === 1 ? "" : "s"}</>
            ) : (
              <>any attached module</>
            )}{" "}
            will lose access immediately. This can't be undone from the UI.
          </p>
          <label className="grid gap-1.5">
            <span className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground">
              Type <code className="text-rose-600 font-mono">DELETE</code> to confirm
            </span>
            <input
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder="DELETE"
              autoFocus
              spellCheck={false}
              data-testid="ws-delete-confirm-input"
              className="rounded-lg border-2 border-rose-300 px-3 py-2 text-sm font-mono uppercase tracking-widest focus:outline-none focus:border-rose-500"
            />
          </label>
        </div>
        <div className="px-6 py-4 border-t border-shell-border flex items-center justify-end gap-2 bg-muted/30">
          <button
            type="button" onClick={onClose}
            data-testid="ws-delete-cancel"
            className="text-xs font-semibold text-muted-foreground border border-shell-border rounded-full px-4 py-2 hover:text-foreground"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canDelete || busy}
            data-testid="ws-delete-confirm"
            className="inline-flex items-center gap-1.5 rounded-full bg-rose-600 hover:bg-rose-700 disabled:bg-rose-300 disabled:cursor-not-allowed text-white text-sm font-semibold px-4 py-2 transition"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
            {busy ? "Deleting…" : "Delete worksheet"}
          </button>
        </div>
      </form>
    </div>
  );
}


/* Read-only inline list of interactive Course-Workbook worksheets for a
 * given module. These are structured Q&A worksheets defined in the LMS
 * (workbook_worksheets collection) — the admin console surfaces them
 * here so admins see the canonical curriculum content next to any file
 * templates they've uploaded. Clicking a row opens the workbook viewer
 * in a new tab so the admin can preview exactly what members see.
 * Editing / renaming lives in the LMS content pipeline, not here. */
function WorkbookWorksheetList({ rows, moduleSlug }) {
  if (!rows || rows.length === 0) return null;
  return (
    <ul
      className="divide-y divide-shell-border border-t border-shell-border bg-primary/[0.03]"
      data-testid={`ws-workbook-list-${moduleSlug}`}
    >
      {rows.map((w) => (
        <li
          key={w.worksheet_id}
          className="flex items-center gap-3 px-3 py-2"
          data-testid={`ws-workbook-row-${w.worksheet_id}`}
        >
          <span
            className="w-8 h-8 rounded-lg bg-sky-100 text-sky-700 grid place-items-center flex-shrink-0"
            title="Interactive Course Workbook worksheet"
          >
            <Sparkles className="w-4 h-4" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-semibold text-sm truncate">{w.title}</p>
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-sky-700 bg-sky-100/70 rounded-full px-2 py-0.5">
                Workbook · W{w.worksheet_number}
              </span>
            </div>
            <p className="text-[11px] text-muted-foreground line-clamp-1">
              {(w.purpose || w.outcome || w.deliverable || "").split("\n")[0]}
            </p>
            <p className="text-[10px] text-muted-foreground mt-0.5">
              {w.estimated_time ? <>⏱ {w.estimated_time}</> : null}
              {w.difficulty ? <> · {w.difficulty}</> : null}
              {typeof w.field_count === "number" ? <> · {w.field_count} fields</> : null}
            </p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <a
              href={`/workbook/${w.worksheet_id}`}
              target="_blank"
              rel="noreferrer"
              data-testid={`ws-workbook-open-${w.worksheet_id}`}
              className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
              title="Open interactive worksheet in a new tab"
            >
              <ArrowRight className="w-3.5 h-3.5" />
            </a>
          </div>
        </li>
      ))}
    </ul>
  );
}

